import React, { useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Tooltip
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import toast from 'react-hot-toast';

import googlecalendar from '../../../assets/googlecalendar.png';
import {
  disconnectClientGoogleCalendarIntegration,
  getClientGoogleCalendarIntegrationStatus,
  startClientGoogleCalendarIntegration
} from '../../../helper/api.helper';

const defaultGoogleCalendarState = { connected: false };

const GoogleCalendarIntegration = () => {
  const [googleCalendar, setGoogleCalendar] = useState(defaultGoogleCalendarState);
  const [googleCalendarOpen, setGoogleCalendarOpen] = useState(false);
  const [googleCalendarEmail, setGoogleCalendarEmail] = useState('');
  const [googleCalendarLoading, setGoogleCalendarLoading] = useState(true);

  const loadGoogleCalendarStatus = async () => {
    setGoogleCalendarLoading(true);
    const res = await getClientGoogleCalendarIntegrationStatus();
    const data = await res.json();
    if (data.status === 'fail') {
      throw Error(data.message || 'Failed to fetch Google Calendar status');
    }
    setGoogleCalendar(data.data || defaultGoogleCalendarState);
    setGoogleCalendarLoading(false);
  };

  useEffect(() => {
    loadGoogleCalendarStatus().catch(() => {
      setGoogleCalendar(defaultGoogleCalendarState);
      setGoogleCalendarLoading(false);
    });
  }, []);

  useEffect(() => {
    const handleGoogleCalendarEvent = (payload) => {
      if (payload?.type !== 'detalks-google-calendar') return;
      if (payload?.status === 'success') {
        loadGoogleCalendarStatus()
          .then(() => {
            toast.success('Google Calendar connected');
          })
          .catch((err) => {
            toast.error(err.message);
          });
        return;
      }
      toast.error(payload?.message || 'Google Calendar connection failed');
    };

    const handleGoogleCalendarMessage = (event) => {
      handleGoogleCalendarEvent(event.data);
    };

    const handleGoogleCalendarStorage = (event) => {
      if (event.key !== 'detalks-google-calendar' || !event.newValue) return;
      try {
        handleGoogleCalendarEvent(JSON.parse(event.newValue));
      } catch (err) {
        console.error('Google Calendar: failed to parse storage event', err);
      }
    };

    window.addEventListener('message', handleGoogleCalendarMessage);
    window.addEventListener('storage', handleGoogleCalendarStorage);
    return () => {
      window.removeEventListener('message', handleGoogleCalendarMessage);
      window.removeEventListener('storage', handleGoogleCalendarStorage);
    };
  }, []);

  const connectGoogleCalendar = async () => {
    const normalizedEmail = googleCalendarEmail.trim().toLowerCase();
    if (!normalizedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      throw Error('Enter a valid Google account email.');
    }
    const popup = window.open(
      '',
      'detalks-google-calendar',
      'width=560,height=720,resizable=yes,scrollbars=yes'
    );
    if (!popup) {
      throw Error('Popup blocked. Please allow popups and try again.');
    }
    const res = await startClientGoogleCalendarIntegration({ googleEmail: normalizedEmail });
    const data = await res.json();
    if (data.status === 'fail') {
      popup.close();
      throw Error(data.message || 'Failed to start Google Calendar integration');
    }
    popup.location = data.data?.authUrl;
    setGoogleCalendarOpen(false);
  };

  const disconnectGoogleCalendar = () => {
    toast.promise(
      disconnectClientGoogleCalendarIntegration()
        .then((res) => res.json())
        .then((res) => {
          if (res.status === 'fail') {
            throw Error(res.message);
          }
          setGoogleCalendar(defaultGoogleCalendarState);
        }),
      {
        loading: 'Disconnecting Google Calendar',
        success: 'Google Calendar disconnected',
        error: (err) => err.message
      }
    );
  };

  return (
    <>
      <div className="integration-item margin-top--sm">
        <img src={googlecalendar} alt="Google Calendar Icon" />
        <h4 className="margin-top--xs">Google Calendar</h4>
        <p style={{ marginBottom: '30px' }}>
          Seamlessly sync your appointments and manage your schedule effortlessly. Connect your
          account to ensure all your sessions are organized in one place.
        </p>
        <div className="helper-row">
          <Tooltip
            title="Google may ask you to confirm calendar access on the next screen before the connection completes."
            arrow>
            <InfoOutlinedIcon className="helper-icon" />
          </Tooltip>
          <p className="helper-copy">
            Check the Google Calendar permission checkbox on the next screen when prompted. If
            missed then disconnect and reconnect again.
          </p>
        </div>
        <div className="action-slot">
          {googleCalendarLoading ? (
            <CircularProgress size={20} />
          ) : googleCalendar?.connected ? (
            <div className="flex-container action-buttons align-center">
              <div>
                <p style={{ color: '#007aff', marginBottom: 0 }}>Connected</p>
                {googleCalendar?.googleEmail ? (
                  <p className="connected-email">{googleCalendar.googleEmail}</p>
                ) : null}
              </div>
              <Button
                size="small"
                color="error"
                variant="outlined"
                onClick={disconnectGoogleCalendar}>
                Disconnect
              </Button>
            </div>
          ) : (
            <Button size="small" variant="contained" onClick={() => setGoogleCalendarOpen(true)}>
              Connect
            </Button>
          )}
        </div>
      </div>

      <Dialog open={googleCalendarOpen} onClose={() => setGoogleCalendarOpen(false)} fullWidth>
        <DialogTitle>Connect Google Calendar</DialogTitle>
        <DialogContent>
          <p className="dialog-copy">
            Enter the Google account email whose calendar should show DeTalks bookings.
          </p>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Google account email"
            type="email"
            value={googleCalendarEmail}
            onChange={(event) => setGoogleCalendarEmail(event.target.value)}
          />
        </DialogContent>
        <DialogActions style={{ marginRight: '15px', marginBottom: '15px' }}>
          <Button onClick={() => setGoogleCalendarOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() =>
              toast.promise(connectGoogleCalendar(), {
                loading: 'Opening Google Calendar',
                success: 'Google sign-in opened',
                error: (err) => err.message
              })
            }>
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      <style jsx>{`
        .integration-item {
          display: flex;
          flex-direction: column;
          max-width: 320px;
          min-width: 200px;
          border-radius: 10px;
          border: 1px solid var(--border-gray);
          padding: 10px;
        }
        .integration-item img {
          width: 70px;
        }
        .action-slot {
          margin-top: auto;
        }
        .action-buttons {
          justify-content: space-between;
        }
        .helper-row {
          align-items: flex-start;
          color: var(--font-gray);
          display: flex;
          gap: 8px;
          margin: -20px 0 20px;
        }
        .helper-icon {
          color: var(--font-gray);
          cursor: pointer;
          font-size: 18px;
          margin-top: 1px;
        }
        .helper-copy {
          color: var(--font-gray);
          font-size: 12px;
          line-height: 1.4;
          margin: 0;
        }
        .connected-email {
          color: var(--font-gray);
          font-size: 12px;
          margin-top: 4px;
        }
        .dialog-copy {
          color: var(--font-gray);
          margin: 0 0 8px;
        }
      `}</style>
    </>
  );
};

export default GoogleCalendarIntegration;
